import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useParams } from 'react-router-dom';
import cn from 'classnames';

import SalonModal from 'components/dialog/SalonModal.component';
import Button from 'components/form/Button.component';
import Icon from 'components/generic/Icon.component';
import IconStatus from 'components/generic/IconStatus.component';
import LINK from 'constants/link.const';
import { useTranslation } from 'providers/GILT.provider';
import { selectCurrentSalonEnabledPinAccess } from 'selectors/currentSalon.selectors';
import selectLockEnabled from 'selectors/selectLockEnabled';
import selectSupportedSalons from 'selectors/selectSupportedSalons';
import fromAnyToInteger from 'utils/converters/fromAnyToInteger';
import withDefaultPrevented from 'utils/dom/withDefaultPrevented';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import linkTo from 'utils/links/linkTo.util';
import logger from 'utils/logger.util';
import checkSupportSalon from 'utils/salons/checkSupportSalon';

import { uiSetLock } from 'lib/actions';

import CN from './PadLock.module.scss';

const L = logger('PadLock');

const PadLock = ({ inMenu }) => {
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const params = useParams();
  const lockEnabled = useSelector(selectLockEnabled);
  const supportSalons = useSelector(selectSupportedSalons);
  const enabledPinAccess = useSelector(selectCurrentSalonEnabledPinAccess);

  const showPopup = useCallback(() => setIsPopupVisible(false), []);
  const hidePopup = useCallback(() => setIsPopupVisible(true), []);

  const salonId = fromAnyToInteger(params.id);
  const isSupport = checkSupportSalon({ salons: supportSalons, salonId });
  const settingStylistLink = linkTo({
    pattern: LINK.settingStylist,
    params: { salonId },
    fault: '#',
  });

  const handleClose = useCallback(() => showPopup(), [showPopup]);

  const handleClick = withDefaultPrevented(() => {
    if (lockEnabled === true && isSupport !== true) {
      dispatch(uiSetLock(true));
    } else {
      hidePopup();
    }
  });

  L.debug('()', { isPopupVisible, props: { lockEnabled, params, inMenu } }); //  return null

  const containerClasses = cn({
    [CN.component]: true,
    [CN.inMenu]: inMenu,
  });

  //NOTE: If enabled_pin_access: false -> do not render the component
  if (!enabledPinAccess) return null;

  return (
    <div data-bem="PadLock" className={containerClasses}>
      <NavLink to="#" onClick={handleClick}>
        <Icon variant="padLockV2" selected={!isSupport} width="xl" />
      </NavLink>

      <SalonModal tag="ModalsPanel__PadLock" isOpen={isPopupVisible} withFooter>
        <SalonModal.Header>
          <div className={CN.closeButtonContainer}>
            <Button variant="empty" onClick={handleClose}>
              <Icon variant="close" variantInvert />
            </Button>
          </div>
        </SalonModal.Header>
        <SalonModal.Body>
          <IconStatus variant="alert" width="lg">
            <Icon variant="alert" width="l1" />
          </IconStatus>
          <div className={CN.modalContent}>
            <div className={CN.modalTitle}>{t('composite.padLockModal.title')}</div>
            <div className={CN.modalText}>{t('composite.padLockModal.description1')} </div>
            <p />
            <div className={CN.modalText}>{t('composite.padLockModal.description2')}</div>
          </div>
        </SalonModal.Body>
        <SalonModal.Footer>
          <div
            className={cn({
              [CN.modalButtons]: true,
            })}
          >
            <NavLink to={settingStylistLink}>
              <Button variant="primaryForce" onClick={alwaysNoop} xSpace>
                {t('composite.padLockModal.button')}
              </Button>
            </NavLink>
          </div>
        </SalonModal.Footer>
      </SalonModal>
    </div>
  );
};

export default PadLock;
